/**
 * The tenant adapter for the shared <LibraryDocumentsView scope>
 * (Library-PR-X3 / Phase 4) — the Library's Documents page.
 *
 * An adapter implements `LibraryDocumentsAdapter` from
 * `./LibraryDocumentsView.types`: it smooths over the scope's data source,
 * mutation wiring, affordance flags and copy so the shared body sees ONE
 * normalized contract. The System Library's adapter is
 * `./LibraryDocumentsView.sysadmin.tsx`.
 *
 * THE SERVER ANSWERS (Library LB6-T2, acceptance L6-15 .. L6-19). The list is
 * ONE request — GET /knowledge/documents with the folder, kind, status, text,
 * order and page the person asked for (lib/use-library-documents.ts) — and
 * every kind is in it: an article is a document (decision 19) and is listed
 * with its stored kind and its state (`articleState`). The header's counts
 * are the server's `countsByKind`; the filters' option lists are the shared
 * schemas' (DocumentKindSchema, DocumentStatusSchema). Nothing here filters,
 * orders or counts the rows it was handed — scripts/ci/no-web-derivation-lint.mjs
 * (W6) fails the run if that comes back.
 */
import { useMemo } from 'react';
import { FolderInput, FolderPlus, Trash2 } from 'lucide-react';
import {
  ArticleStateSchema,
  DOCUMENT_LIST_SORTS,
  DOCUMENT_SEARCH_MAX_LENGTH,
  DocumentKindSchema,
  DocumentListSortSchema,
  DocumentStatusSchema,
  type DocumentShape,
  type DocumentStatus,
} from '@argusai/shared';
import { ARTICLE_STATE_LABEL } from './document-kind';
import { Badge as BadgeImport } from '@/components/ui/badge';
import { useHasPermission } from '@/lib/use-has-permission';
import { useLibraryFolders } from '@/lib/use-library-folders';
import { useLibraryDocuments, type LibraryDocumentsQuery } from '@/lib/use-library-documents';
import type {
  BulkActionDescriptor,
  DocCounts,
  FilterOption,
  LibraryDocumentsAdapter,
  LibraryDocumentsAllows,
  LibraryListSort,
  ListContext,
  ListDocsQuery,
  ListDocsResult,
  NormalizedLibraryDoc,
} from './LibraryDocumentsView.types';

// ─── The tenant list's vocabularies — read from the shared schemas (L6-19) ──

/** How each ingestion status is labelled. A Record over the schema: a new status without a label fails the typecheck. */
const DOCUMENT_STATUS_LABEL: Readonly<Record<DocumentStatus, string>> = Object.freeze({
  pending: 'Pending',
  processing: 'Processing',
  ready: 'Ready',
  failed: 'Failed',
});

/**
 * The Status filter answers what the Status column shows (LB6T2-BUG-02 /
 * REV-05): a file or a synced document shows its INGESTION status, an article
 * its STATE. So it offers every ingestion status (DocumentStatusSchema,
 * `pending` included — L6-19), then every article state (ArticleStateSchema),
 * each in its schema's order. The server lists no article under an ingestion
 * status and only articles under a state (GET /knowledge/documents `status` /
 * `articleState`), so "Ready" never lists a draft and "Draft" lists the drafts.
 */
export const TENANT_STATUS_OPTIONS: readonly FilterOption[] = Object.freeze([
  ...DocumentStatusSchema.options.map((s) => ({ value: s, label: DOCUMENT_STATUS_LABEL[s] })),
  ...ArticleStateSchema.options.map((s) => ({ value: s, label: ARTICLE_STATE_LABEL[s] })),
]);

/** The Type filter: every Document kind, in the schema's order. */
export const TENANT_KINDS = Object.freeze([...DocumentKindSchema.options]);

/** The tenant list opens in title order (the order the page always opened in). */
export const TENANT_DEFAULT_SORT: LibraryListSort = Object.freeze({ key: 'title', direction: 'asc' });

/** A page of the list — the server's page size ("Show more" asks for the next). */
export const TENANT_LIST_PAGE_SIZE = 50;

/**
 * Where the article reader was opened from: its Previous / Next follow this
 * list (L6-16) — its folder, its text search, its article state and its order
 * (LB6T2-BUG-04 / REV-03). An ingestion status is not carried: the server
 * lists no article under one, so no article is opened from such a list.
 */
export interface ArticleReaderContext {
  folderId?: string;
  /** The list's Tag filter (decision 60, LB6c-T1): a tag id, or absent. */
  tagId?: string;
  articleState?: LibraryDocumentsQuery['articleState'];
  search?: string;
  sort?: LibraryDocumentsQuery['sort'];
  sortDir?: LibraryDocumentsQuery['sortDir'];
}

/** The server's folder parameter for the View's folder filter. */
export function folderParam(folderFilter: string): string | undefined {
  if (folderFilter === 'all') return undefined;
  if (folderFilter === '__root__') return 'root';
  return folderFilter;
}

/** The server's order for the View's sort — a column the server cannot order by falls back to the default. */
function sortParam(sort: LibraryListSort): Pick<LibraryDocumentsQuery, 'sort' | 'sortDir'> {
  const key = DocumentListSortSchema.safeParse(sort.key);
  return {
    sort: key.success ? key.data : (TENANT_DEFAULT_SORT.key as LibraryDocumentsQuery['sort']),
    sortDir: sort.direction,
  };
}

/** What the View asks, as GET /knowledge/documents' parameters (L6-15). */
export function tenantListQuery(query: ListDocsQuery): LibraryDocumentsQuery {
  // The Status filter's value is an ingestion status or an article state —
  // the two alphabets share no value (TENANT_STATUS_OPTIONS).
  const status = DocumentStatusSchema.safeParse(query.statusFilter);
  const articleState = ArticleStateSchema.safeParse(query.statusFilter);
  return {
    folderId: folderParam(query.folderFilter),
    kind: query.kindFilter,
    status: status.success ? status.data : undefined,
    articleState: articleState.success ? articleState.data : undefined,
    // The Tag filter (LB6c-T1): one tag's id, answered by the server's one
    // by-tag predicate — never a filter over rows already loaded.
    tagId: query.tagIdFilter && query.tagIdFilter !== 'all' ? query.tagIdFilter : undefined,
    // Never longer than the server takes (the search box stops there too).
    search: query.search.trim().slice(0, DOCUMENT_SEARCH_MAX_LENGTH) || undefined,
    ...sortParam(query.sort),
  };
}

/** The reader's context for a row opened from this list: the same question, for its articles. */
export function articleReaderContext(context: ListContext | undefined): ArticleReaderContext | undefined {
  if (!context) return undefined;
  const asked = tenantListQuery(context);
  return {
    folderId: asked.folderId,
    tagId: asked.tagId,
    articleState: asked.articleState,
    search: asked.search,
    sort: asked.sort,
    sortDir: asked.sortDir,
  };
}

/** One row of the server's answer, as the shared body draws it. */
export function tenantRow(d: DocumentShape, folderNames: ReadonlyMap<string, string>): NormalizedLibraryDoc {
  const isArticle = d.kind === 'article';
  return {
    id: d.id,
    title: d.title,
    kind: d.kind,
    // An article shows its state (draft / published …); a file or synced
    // document its ingestion status. An article the server sent no state for
    // shows what the server did send — never a state made up here.
    status: isArticle ? d.articleState ?? d.status : d.status,
    ingestStatus: isArticle ? null : d.status,
    authorLabel: d.uploadedBy,
    updatedAt: d.updatedAt ?? d.createdAt,
    folderId: d.folderId ?? null,
    folderName: d.folderId ? folderNames.get(d.folderId) ?? '—' : '',
    ...(isArticle
      ? {}
      : { fileName: d.fileName, fileSize: d.fileSize, chunkCount: d.chunkCount, mimeType: d.mimeType }),
    raw: d,
  };
}

export interface TenantAdapterCallbacks {
  /** Open the upload dialog. */
  onOpenUpload: () => void;
  /** Open the WikiEditor for a new article (router file handles the local-draft creation). */
  onOpenCreateArticle: () => void;
  /** Open the "Create folder" dialog from the New dropdown. */
  onOpenCreateFolder: () => void;
  /** Open an article (the reader route) by its document id, with the list it was opened from. */
  onSelectArticle: (articleId: string, context?: ArticleReaderContext) => void;
  /** Open the uploaded-doc detail page. */
  onOpenUploadedDoc: (id: string) => void;
  /** Delete a wiki article (the View has already confirmed in its dialog). */
  onDeleteArticle: (id: string) => void;
  /** Delete an uploaded doc (the View has already confirmed in its dialog). */
  onDeleteUploaded: (id: string) => void;
  /**
   * Trigger the move-to-folder bulk dialog. Route owns the dialog state;
   * the adapter receives the selection and forwards it.
   */
  onBulkMove: (selectedIds: Set<string>, selectedDocs: NormalizedLibraryDoc[]) => void;
  /** Trigger the bulk delete confirmation + action. */
  onBulkDelete: (selectedIds: Set<string>, selectedDocs: NormalizedLibraryDoc[]) => void;
  /** Track in-flight bulk work so the action buttons can render busy. */
  bulkBusy: boolean;
  /** Open the per-doc share dialog. */
  onShareDocument: (doc: NormalizedLibraryDoc) => void;
  /**
   * Row-level move-to-folder. Triggered from the row dropdown menu;
   * route owns the dialog state.
   */
  onRowMove: (doc: NormalizedLibraryDoc) => void;
  /**
   * Row-level publish for a wiki article (P1 #1 from Round 1 Library
   * audit, 2026-05-13). Route owns the API call + list invalidation.
   */
  onPublishArticle: (doc: NormalizedLibraryDoc) => void;
  /**
   * Row-level unpublish (state -> draft) for a wiki article. The route
   * confirms it in an in-app dialog (no native confirm — LB6-T2, L6-18) and
   * sends the unpublish through the article route the server registers.
   */
  onUnpublishArticle: (doc: NormalizedLibraryDoc) => void;
  /**
   * Row-level "View" — open the uploaded doc detail page. Same target as
   * the title click; surfaced as a discoverable menu item per codex
   * issue #712. Article rows already open the reader on title click.
   */
  onViewUploaded: (doc: NormalizedLibraryDoc) => void;
  /**
   * Row-level "Download" — fetch the original file via
   * `/knowledge/documents/:id/file`. Codex #712. The route file owns
   * the fetch + toast feedback so the adapter stays presentation-only.
   */
  onDownloadUploaded: (doc: NormalizedLibraryDoc) => void;
}

/**
 * Tenant adapter — wires shared view to tenant endpoints.
 *
 * The tenant route owns a great deal more state than sysadmin (active
 * folder, view mode, active doc, share dialogs, etc.), so this adapter only
 * takes care of the LIST surface. The route file keeps the
 * sidebar/editor/share dialogs orchestration.
 */
export function useTenantAdapter(callbacks: TenantAdapterCallbacks): LibraryDocumentsAdapter {
  // Library LB6-T3 / L6-04 — each list control asks EXACTLY the key its route
  // declares (LIBRARY_CONTROLS in @argusai/shared, held to the declarations by
  // packages/api/src/__tests__/library-route-keys.test.ts). A control the
  // person may not use is not drawn.
  //   upload, create folder, move, edit metadata → write:library.documents
  //   delete a file or synced document            → delete:library.documents
  //   write / edit / publish / delete an article  → write:wiki
  //   share a document or an article              → share:library
  const canWriteDocuments = useHasPermission('write', 'library.documents');
  const canDeleteDocuments = useHasPermission('delete', 'library.documents');
  const canWriteArticles = useHasPermission('write', 'wiki');
  const canShare = useHasPermission('share', 'library');
  return useMemo<LibraryDocumentsAdapter>(() => {
    const allows: LibraryDocumentsAllows = {
      bulkSelect: canWriteDocuments || canDeleteDocuments,
      upload: canWriteDocuments,
      writeArticle: canWriteArticles,
      edit: (doc) => (doc.kind === 'article' ? canWriteArticles : canWriteDocuments),
      publish: (doc) => doc.kind === 'article' && canWriteArticles,
      delete: (doc) => (doc.kind === 'article' ? canWriteArticles : canDeleteDocuments),
    };
    const adapter: LibraryDocumentsAdapter = {
      allows: Object.freeze(allows),
      // ── Page chrome ──
      pageTitle: 'All Documents',
      // The server's counts for this query (countsByKind, L6-15). L19-14 —
      // 'N synced' counts the rows of kind 'synced'.
      buildStatsLine: (counts: DocCounts) =>
        `${counts.total} items · ${counts.byKind.article} articles · ${counts.byKind.file} uploaded · ${counts.byKind.synced} synced`,
      emptyStateMessage:
        'No documents yet. Upload a file or write an article to get started.',
      emptyFilterMessage: 'No documents match your filters.',
      uploadDialogTitle: 'Upload Document',
      uploadDialogDescription: 'Upload a file to your tenant library.',

      // ── Affordance flags ──
      showSidebar: true,
      showStarColumn: true,
      showPublishLifecycle: false,
      showAuthorColumn: true,
      bulkAction: 'move-publish-mixed',
      supportedKinds: TENANT_KINDS,
      statusOptions: TENANT_STATUS_OPTIONS,
      tagFilter: true,
      sortKeys: DOCUMENT_LIST_SORTS,
      defaultSort: TENANT_DEFAULT_SORT,

      // ── Data ──
      useList: (query) => useTenantList(query),

      // ── Row event handlers ──
      onEditRow: (doc, context) => {
        if (doc.kind === 'article') {
          callbacks.onSelectArticle(doc.id, articleReaderContext(context));
        } else {
          callbacks.onOpenUploadedDoc(doc.id);
        }
      },
      onDeleteRow: (doc) => {
        if (doc.kind === 'article') callbacks.onDeleteArticle(doc.id);
        else callbacks.onDeleteUploaded(doc.id);
      },
      // P1 #1 (2026-05-13) — Publish row action for tenant articles. Only an
      // article has a draft/published state (its `status` here is its
      // `articleState`); a file's status is its ingestion status and never
      // matches the `status === 'draft'` guard inside the shared View body.
      onPublishRow: (doc) => {
        if (doc.kind === 'article') callbacks.onPublishArticle(doc);
      },
      onUnpublishRow: (doc) => {
        if (doc.kind === 'article') callbacks.onUnpublishArticle(doc);
      },

      // Share every kind (an article is a document — LX-03), for a person who
      // holds share:library; Move (files and synced documents) for one who
      // holds write:library.documents. Absent otherwise: the item is not drawn.
      onShareRow: canShare ? callbacks.onShareDocument : undefined,
      onMoveRow: canWriteDocuments ? callbacks.onRowMove : undefined,
      // #712 — View + Download row items. Wired only for tenant scope;
      // sysadmin adapter leaves them undefined so the menu items hide.
      onViewRow: callbacks.onViewUploaded,
      onDownloadRow: callbacks.onDownloadUploaded,

      // ── New-dropdown handlers ──
      onOpenUpload: callbacks.onOpenUpload,
      onOpenCreateArticle: callbacks.onOpenCreateArticle,
      newMenuExtras: canWriteDocuments
        ? [
            {
              id: 'create-folder',
              label: 'Create Folder',
              icon: FolderPlus,
              onClick: callbacks.onOpenCreateFolder,
            },
          ]
        : [],

      // ── Bulk actions — tenant exposes Move + Delete (#10) ──
      isSelectable: (doc) => doc.kind !== 'article', // tenant excludes articles
      // Move asks write:library.documents and Delete delete:library.documents
      // — what the bulk route asks for each verb (bulk-escalation.test.ts).
      getBulkActions: ({ selectedIds, selectedDocs }) => {
        void selectedIds; void selectedDocs;
        const actions: BulkActionDescriptor[] = [];
        if (canWriteDocuments) actions.push(
          {
            id: 'move',
            label: 'Move to folder…',
            icon: FolderInput,
            title: 'Move the selected documents to a folder',
            busy: callbacks.bulkBusy,
            disabled: callbacks.bulkBusy,
            onClick: (ids, docs) => callbacks.onBulkMove(ids, docs),
          },
        );
        if (canDeleteDocuments) actions.push(
          {
            id: 'delete',
            label: 'Delete',
            icon: Trash2,
            title: 'Delete the selected documents',
            destructive: true,
            busy: callbacks.bulkBusy,
            disabled: callbacks.bulkBusy,
            onClick: (ids, docs) => callbacks.onBulkDelete(ids, docs),
          },
        );
        return actions;
      },

      // ── Custom cells — tenant ready/processing/draft badges ──
      renderStatusCell: (doc) => <TenantStatusBadge status={doc.status} />,
      renderTitleSubline: (doc) => {
        if ((doc.kind === 'file' || doc.kind === 'synced') && doc.fileName) {
          const parts: string[] = [doc.fileName];
          if (doc.chunkCount) parts.push(`${doc.chunkCount} chunks`);
          return parts.join(' · ');
        }
        return null;
      },
    };
    return Object.freeze(adapter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callbacks.onOpenCreateArticle,
    callbacks.onOpenCreateFolder,
    callbacks.onSelectArticle,
    callbacks.onOpenUploadedDoc,
    callbacks.onOpenUpload,
    callbacks.onDeleteArticle,
    callbacks.onDeleteUploaded,
    callbacks.onBulkMove,
    callbacks.onBulkDelete,
    callbacks.onShareDocument,
    callbacks.onRowMove,
    callbacks.onPublishArticle,
    callbacks.onUnpublishArticle,
    callbacks.onViewUploaded,
    callbacks.onDownloadUploaded,
    callbacks.bulkBusy,
    canWriteDocuments,
    canDeleteDocuments,
    canWriteArticles,
    canShare,
  ]);
}

// ─── Internal: the tenant list — the server's answer ───────────────────────

function useTenantList(query: ListDocsQuery): ListDocsResult {
  // Decision 18 — the one folder list (lib/use-library-folders.ts) names each
  // row's folder; a failed folder read is the list's error, never a '—'.
  const foldersQ = useLibraryFolders();
  const serverQuery = useMemo(
    () => tenantListQuery(query),
    // The query object is rebuilt each render; its fields are the identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.folderFilter, query.kindFilter, query.statusFilter, query.tagIdFilter, query.search, query.sort.key, query.sort.direction],
  );
  const listQ = useLibraryDocuments(serverQuery, TENANT_LIST_PAGE_SIZE);

  const folderNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of foldersQ.data ?? []) m.set(f.id, f.name);
    return m;
  }, [foldersQ.data]);

  const pages = listQ.data?.pages;
  const docs = useMemo<NormalizedLibraryDoc[]>(
    () => (pages ?? []).flatMap((p) => p.data).map((d) => tenantRow(d, folderNames)),
    [pages, folderNames],
  );

  // The counts are the server's, for this query — the latest page's answer.
  const last = pages && pages.length > 0 ? pages[pages.length - 1] : undefined;
  const counts = useMemo<DocCounts | null>(
    () => (last ? { total: last.total, byKind: last.countsByKind } : null),
    [last],
  );

  const folders = useMemo(
    () => (foldersQ.data ?? []).map((f) => ({ id: f.id, name: f.name })),
    [foldersQ.data],
  );

  // A failed read with nothing to show is the list's error. A query keeps
  // its `error` after a failed "Show more" or background refresh while its
  // rows are still there: those rows stay on screen and the failure is said
  // beside them (LB6T2-BUG-06) — the same rule the page applies to the
  // folder query (`foldersQ.isError && !foldersQ.data`).
  const foldersFailed = foldersQ.isError && !foldersQ.data;
  const listFailed = listQ.isError && !listQ.data;
  const error = (foldersFailed ? foldersQ.error : null) || (listFailed ? listQ.error : null) || null;
  const loadMoreError = error ? null : (listQ.isError ? listQ.error : null) || (foldersQ.isError ? foldersQ.error : null);
  return {
    docs,
    counts: error ? null : counts,
    folders,
    loading: foldersQ.isLoading || listQ.isLoading,
    error,
    loadMoreError,
    retry: () => {
      if (foldersQ.isError) void foldersQ.refetch();
      if (listQ.isFetchNextPageError) void listQ.fetchNextPage();
      else if (listQ.isError) void listQ.refetch();
    },
    hasMore: listQ.hasNextPage === true,
    loadingMore: listQ.isFetchingNextPage,
    loadMore: () => {
      void listQ.fetchNextPage();
    },
  };
}

// ─── Tenant status badge (matches pre-refactor renderer) ───────────────────

function TenantStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'ready':
    case 'published':
      return (
        <BadgeImport
          variant="outline"
          className="text-[9px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
        >
          {status === 'ready' ? 'Ready' : 'Published'}
        </BadgeImport>
      );
    case 'processing':
      return (
        <BadgeImport
          variant="outline"
          className="text-[9px] bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
        >
          Processing
        </BadgeImport>
      );
    case 'draft':
      return (
        <BadgeImport
          variant="outline"
          className="text-[9px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
        >
          Draft
        </BadgeImport>
      );
    case 'failed':
      return (
        <BadgeImport
          variant="outline"
          className="text-[9px] bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20"
        >
          Failed
        </BadgeImport>
      );
    default:
      return (
        <BadgeImport variant="outline" className="text-[9px]">
          {status}
        </BadgeImport>
      );
  }
}
